/**
 * AudioMeter — a Google-Meet-style live mic-level indicator (animated bars).
 *
 * `useAudioLevel` taps a stream's first audio track through a WebAudio
 * AnalyserNode and returns a smoothed 0..1 level. It works for BOTH the local
 * mic stream and a remote (peer) stream — for a remote stream the audio must
 * already be playing through an <audio>/<video> sink (the call's hidden audio
 * element) for the analyser to receive data, which it is.
 *
 * Everything no-ops gracefully when WebAudio is unavailable (tests / old
 * browsers) — the level stays 0 and the bars render flat.
 */
import { useEffect, useRef, useState } from 'react';
import { Box } from '@mui/material';

function getAudioCtor(): typeof AudioContext | undefined {
  if (typeof AudioContext !== 'undefined') return AudioContext;
  const w = window as { webkitAudioContext?: typeof AudioContext };
  return typeof w.webkitAudioContext !== 'undefined' ? w.webkitAudioContext : undefined;
}

/** Smoothed 0..1 mic level for a stream. 0 when inactive / no stream / no WebAudio. */
export function useAudioLevel(stream: MediaStream | null, active = true): number {
  const [level, setLevel] = useState(0);
  const rafRef = useRef<number | null>(null);

  useEffect(() => {
    if (!stream || !active || stream.getAudioTracks().length === 0) {
      setLevel(0);
      return;
    }
    const Ctor = getAudioCtor();
    if (!Ctor) return;

    let ctx: AudioContext;
    try {
      ctx = new Ctor();
    } catch {
      return;
    }
    void ctx.resume?.();

    let src: MediaStreamAudioSourceNode;
    try {
      src = ctx.createMediaStreamSource(stream);
    } catch {
      try {
        void ctx.close();
      } catch {
        /* ignore */
      }
      return;
    }

    const analyser = ctx.createAnalyser();
    analyser.fftSize = 256;
    analyser.smoothingTimeConstant = 0.6;
    src.connect(analyser);
    const data = new Uint8Array(analyser.frequencyBinCount);

    const tick = (): void => {
      analyser.getByteTimeDomainData(data);
      let sum = 0;
      for (let i = 0; i < data.length; i++) {
        const v = ((data[i] ?? 128) - 128) / 128;
        sum += v * v;
      }
      const rms = Math.sqrt(sum / data.length);
      // ema-smooth and scale so normal speech lights most bars
      setLevel((prev) => prev * 0.55 + Math.min(1, rms * 4) * 0.45);
      rafRef.current = requestAnimationFrame(tick);
    };
    tick();

    return () => {
      if (rafRef.current !== null) cancelAnimationFrame(rafRef.current);
      rafRef.current = null;
      try {
        src.disconnect();
        analyser.disconnect();
      } catch {
        /* ignore */
      }
      try {
        void ctx.close();
      } catch {
        /* ignore */
      }
      setLevel(0);
    };
  }, [stream, active]);

  return level;
}

export interface AudioMeterProps {
  stream: MediaStream | null;
  /** When false the meter is flat (e.g. muted). */
  active?: boolean;
  color?: string;
  bars?: number;
  height?: number;
}

/** Animated mic-level bars. */
export function AudioMeter({
  stream,
  active = true,
  color = '#3ddc84',
  bars = 4,
  height = 14,
}: AudioMeterProps): JSX.Element {
  const level = useAudioLevel(stream, active);
  // Center bars react strongest (a little EQ shape), like Meet.
  const shape = [0.6, 1, 1, 0.6];
  return (
    <Box
      data-testid="audio-meter"
      data-active={active ? 'true' : 'false'}
      sx={{ display: 'flex', alignItems: 'center', gap: '2px', height }}
    >
      {Array.from({ length: bars }).map((_, i) => {
        const k = shape[i % shape.length] ?? 1;
        const barLevel = Math.max(0, Math.min(1, level * k));
        const h = Math.max(3, 3 + barLevel * (height - 3));
        return (
          <Box
            key={i}
            sx={{
              width: 3,
              height: h,
              borderRadius: 1,
              bgcolor: color,
              opacity: 0.35 + barLevel * 0.65,
              transition: 'height 90ms linear, opacity 90ms linear',
            }}
          />
        );
      })}
    </Box>
  );
}
